import DanaProductCarouselProduct from "@dana/Components/DanaProductCarouselProduct";
import { ProductCarouselProductStyles } from "@dana/Components/DanaProductCarouselProduct";
import NextArrowIcon from "@dana/Icons/NextArrowIcon";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { getResponsiveSpacing } from "@dana/MobiusOverrides/styles/spacing";
import { fontSizes, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    widgetPosition = "widgetPosition",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    visibleElementsTitle = "visibleElementsTitle",
    titleAlignment = "titleAlignment",
    tab1Label = "tab1Label",
    addProductNumbers = "addProductNumbers",
    showViewProduct = "showViewProduct",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.widgetPosition]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.visibleElementsTitle]: string;
        [fields.titleAlignment]: string;
        [fields.tab1Label]: string;
        [fields.addProductNumbers]: string[];
        [fields.showViewProduct]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    productRecommendationWidgets: state.components.productRecommendations.widgets,
    session: state.context.session,
});

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface DanaProductSpotlightCarouselStyles {
    productCarouselContainer?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    carouselArrowWrapper?: InjectableCss;
    prevArrowGridItem?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: LinkPresentationProps;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const DanaProductSpotlightCarouselStyles: DanaProductSpotlightCarouselStyles = {
    productCarouselContainer: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 40px 16px;
                        `,
                        css`
                            padding: 0 16px;
                        `,
                        css`
                            padding: 0 24px;
                        `,
                        css`
                            padding: 0 40px;
                        `,
                    ],
                    "min",
                )}
            ${getResponsiveSpacing("py", "xLarge")};
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 24px;
        `,
    },
    mainContainer: { gap: 0 },
    carouselArrowWrapper: {
        css: css`
            width: 100%;
            max-width: 1440px;
            margin: 0 auto;
            margin-bottom: 16px;
            display: flex;
            column-gap: 8px;
            justify-content: flex-end;
        `,
    },
    prevArrowGridItem: {
        css: css`
            justify-content: flex-start;
        `,
    },
    prevArrowButton: {
        shape: "rectangle",
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            padding: 0;
            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }

            &:not(:disabled) {
                &:hover {
                    background: transparent;
                    border: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    background: transparent;
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                    outline: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }
            }

            &:disabled {
                background: none;
                border: none;
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeBackgrounds.darkGray};
                        }
                    }
                }
            }
        `,
    },
    nextArrowGridItem: {
        css: css`
            justify-content: flex-end;
        `,
    },
    nextArrowButton: {
        shape: "rectangle",
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            padding: 0;
            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }

            &:not(:disabled) {
                &:hover {
                    background: transparent;
                    border: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    background: transparent;
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                    outline: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }
            }

            &:disabled {
                background: none;
                border: none;
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeBackgrounds.darkGray};
                        }
                    }
                }
            }
        `,
    },
    carouselGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding-bottom: 2px;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;

            @media print {
                transform: none !important;
                display: block;
            }
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;

            padding-left: 8px;
            padding-right: 8px;

            @media print {
                width: 50% !important;
                float: left;
                margin-bottom: 16px;
                height: 350px;
            }
        `,
    },
    carouselSlideInner: {
        typographyProps: {
            underline: false,
        },
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;

            border: 1px solid ${themeBackgrounds.lightGray};
            padding: 24px;

            &:hover,
            &:focus-visible {
                border-color: ${themeColors.primary.danaBlue};
                box-shadow: ${elevation.hover};
                outline: none;
            }

            text-decoration: none;

            ${StyledTypography} {
                text-decoration: none;
            }

            &:hover {
                text-decoration: none;
                ${StyledTypography} {
                    text-decoration: none;
                }
            }
        `,
    },
    carouselProductStyles: {
        badgeAreaWrapper: {
            css: css`
                position: initial;

                .badge-wrapper {
                    top: 8px;
                    right: -8px;
                    overflow: visible;
                    width: auto;

                    [class*="TextBadge"] {
                        padding: 4px 16px;
                        position: relative;

                        &::after {
                            content: "";
                            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='10' viewBox='0 0 7 10' fill='none'%3E%3Cpath d='M-4.69387e-05 9.78115L-0.000116752 0.781067L7.00007 9.78119L-4.69387e-05 9.78115Z' fill='%23535353'/%3E%3C/svg%3E");
                            background-repeat: no-repeat;
                            position: absolute;
                            top: -10px;
                            right: -2px;
                            width: 10px;
                            height: 14px;
                            z-index: 5;
                        }
                    }
                }
            `,
        },
        productDescriptionStyles: {
            productDetailDescription: {
                size: fontSizes.large,
                lineHeight: lineHeights.defaultLarge,
                color: themeColors.primary.richBlack,
            },
        },
        productPartNumbersStyles: {
            container: {
                css: css`
                    margin-top: 24px;
                `,
            },
            erpNumberGridItem: {
                css: css`
                    padding: 0;
                `,
            },
            erpNumberValueText: {
                variant: "h3",
                color: themeColors.primary.danaBlue,
                css: css`
                    margin-bottom: 0;
                    &:hover {
                        color: ${themeColors.secondary.danaDarkBlue};
                    }
                `,
            },
        },
        productImageStyles: {
            img: {
                css: css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                min-height: 310px;
                                max-height: 310px;
                                object-fit: contain;
                            `,
                            css`
                                min-height: 310px;
                                max-height: 310px;
                                object-fit: contain;
                            `,
                            css`
                                min-height: 337px;
                                max-height: 337px;
                                object-fit: contain;
                            `,
                            css`
                                min-height: 280px;
                                max-height: 280px;
                                object-fit: contain;
                            `,
                            css`
                                min-height: 280px;
                                max-height: 280px;
                                object-fit: contain;
                            `,
                        ])}

                    @media print {
                        min-height: 190px;
                        max-height: 190px;
                    }
                `,
            },
        },
    },
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const styles = DanaProductSpotlightCarouselStyles;

const DanaProductSpotlightCarousel = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    productRecommendationWidgets,
    sendImpressionTracking,
    session,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);

    const [currentTab, setCurrentTab] = useState("Tab1");

    const getTabsProductNumber = (productNumbers: any[]) => {
        if (!productNumbers || productNumbers.length === 0) {
            return;
        }
        const productsSpotlight = [] as string[];
        productNumbers.forEach(r => {
            productsSpotlight.push(r.fields.productNumbers);
        });
        return productsSpotlight;
    };
    const [title, setTitle] = useState(fields.title);
    const tab1Label = fields.tab1Label;
    const addProductNumbers = fields.addProductNumbers;

    const tabbedProductNumbers = getTabsProductNumber(fields.addProductNumbers) as string[] | undefined;
    const [carouselProducts, setCarouselProducts] = useState(tabbedProductNumbers);
    const [active, setActive] = useState("Tab1");
    const [currentTabLabel, setCurrentTabLabel] = useState(tab1Label);

    const [tabbedCarouselId, setTabbedCarouselId] = useState(id);
    const getProductNumbersList = (productNumbers: any[], tabId: string, tabLabel: string) => {
        if (!productNumbers || productNumbers.length === 0) {
            return;
        }

        setTabbedCarouselId(tabId);
        const productsSpotlight = getTabsProductNumber(productNumbers);
        setCarouselProducts(productsSpotlight);
        setActive(tabId);
        setCurrentTabLabel(tabLabel);
        setCurrentTab(tabId);
    };

    useEffect(() => {
        const productNumbers = carouselProducts;
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: `SpotlightPromotion|${title}|${currentTabLabel}|${currentTab}`,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: fields.widgetPosition,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
            productNumbers,
        } as any);
    }, [carouselProducts]);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: false,
        });
        setCanScroll();
    }, [embla, slidesToScroll, products]);

    useEffect(() => {
        if (
            fields.carouselType !== "recommendedProducts" ||
            !productRecommendationWidgets ||
            fields.widgetPosition > productRecommendationWidgets.length ||
            !products
        ) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        const recommendationIds: number[] = [];
        products?.forEach(o => {
            const id = productRecommendationWidget.recs.find(r => r.refCode === o.detail?.name)?.id;
            if (id) {
                recommendationIds.push(id);
            }
        });

        if (recommendationIds.length) {
            sendImpressionTracking({ recommendationIds });
        }
    }, [fields.carouselType, productRecommendationWidgets, products]);

    const getProductRecommendationId = (product: ProductModel) => {
        if (!productRecommendationWidgets || fields.widgetPosition > productRecommendationWidgets.length) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        return productRecommendationWidget.recs.find(r => r.refCode === product.detail?.name)?.id;
    };

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const showBrandBlocks = fields.showBrandName && products?.some(o => !!o.brand);

    return (
        <StyledWrapper {...styles.productCarouselContainer} className="product-spotlight-carousel-container">
            <>
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
                <ContentWrapper alignment="center" className="wrapper">
                    <Typography {...styles.titleText}>{title}</Typography>
                </ContentWrapper>
                <StyledWrapper className="carousel-arrow-wrapper" {...styles.carouselArrowWrapper}>
                    <StyledWrapper {...styles.prevArrowGridItem}>
                        {products.length > slidesToScroll && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                aria-label={translate("Previous")}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon src={PrevArrowIcon} />
                            </Button>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.nextArrowGridItem}>
                        {products.length > slidesToScroll && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                disabled={!canScrollNext}
                                aria-label={translate("Next")}
                                data-test-selector="nextBtn"
                            >
                                <ButtonIcon src={NextArrowIcon} />
                            </Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <GridContainer {...styles.mainContainer}>
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper className="carousel-wrapper" {...styles.carouselWrapper}>
                            <StyledWrapper className="carouselContainer" ref={emblaRef} {...styles.carouselContainer}>
                                <StyledWrapper data-test-selector="slides" {...styles.carouselSlidesContainer}>
                                    {products.map(product => (
                                        <StyledWrapper
                                            {...styles.carouselSlide}
                                            key={product.id}
                                            style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        >
                                            <Link
                                                href={product?.canonicalUrl}
                                                {...styles.carouselSlideInner}
                                                id={`product_${product.id}`}
                                                data-test-key={product.id}
                                                data-test-selector="carousel_slide"
                                                tabIndex={0}
                                            >
                                                <DanaProductCarouselProduct
                                                    carouselId={id}
                                                    key={product.id}
                                                    product={product}
                                                    showImage={fields.showImage}
                                                    showBrand={showBrandBlocks}
                                                    showTitle={fields.showTitle}
                                                    showPartNumbers={fields.showPartNumbers}
                                                    showPrice={fields.showPrice}
                                                    showAddToCart={fields.showAddToCart}
                                                    showAddToList={fields.showAddToList}
                                                    showImageBadges={fields.showImageBadges}
                                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                                    showTextBadges={fields.showTextBadges}
                                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                                    extendedStyles={styles.carouselProductStyles}
                                                    showQtyBox={false}
                                                />
                                            </Link>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </>
        </StyledWrapper>
    );
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const CarouselSlidesContainer = styled.div`
    display: flex;
`;

const CarouselSlide = styled.div`
    padding: 0;
    position: relative;
    flex: 0 0 auto;
`;

const CarouselSlideInner = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
`;

const ProductImage = styled.div`
    width: 145px;
    height: 145px;
    background-color: #e0e0e0;
`;
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(DanaProductSpotlightCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        displayName: "Dana Products Spotlight Carousel",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.addProductNumbers,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.productNumbers;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "productNumbers",
                        displayName: "",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                tab: basicTab,
                sortOrder: 3,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 15,
                tab: basicTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 16,
                tab: basicTab,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 17,
                tab: basicTab,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 18,
                tab: basicTab,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 19,
                tab: basicTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 20,
                tab: basicTab,
            },
            {
                name: fields.showViewProduct,
                displayName: "View Product",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 21,
                tab: basicTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 22,
                tab: basicTab,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                isEnabled: () => false,
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 23,
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "1",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 24,
                tab: basicTab,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 25,
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "1",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 26,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
